/**
 * PlacerCanvas — the digital aquascape placement workspace. Operators:
 *   • upload aquascape photos that actually SAVE to the Pi (data-URL),
 *   • expand to fullscreen and zoom/pan to place corals precisely,
 *   • resize the coral dots (per-dot + a default size),
 *   • click a dot to pick its photo (frag/colony), move, or remove it.
 * There is intentionally no "Load v3 Expert Layout" button — this is a tool
 * for everyone, not a personal preset. All state changes write through to
 * the Pi via the workspace controller passed down from the page.
 */
import { useEffect, useMemo, useRef, useState } from 'react';

import { Button } from '../components/Button';
import { Select } from '../components/ui/Select';

import { COLOR_HEX, findCoral, placementPhotoUrl } from './catalog';
import { ColorDots, GrowthPill } from './chips';
import { CoralThumb } from './CoralThumb';
import { fileToDownscaledDataUrl } from './image';
import { genId } from './ids';
import {
  MAX_DOT_SIZE,
  MIN_DOT_SIZE,
  addAquascape,
  addPlacement,
  clearPlacements,
  placementsFor,
  removeAquascape,
  removePlacement,
  renameAquascape,
  setActiveAquascape,
  setDefaultDotSize,
  updatePlacement,
} from './workspace';
import type { SavedLayout } from './savedLayouts';
import type {
  Coral,
  Placement,
  PlacementPhoto,
  Workspace,
} from './types';

type DisplayMode = 'thumbnail' | 'dot';

export interface PlacerCanvasProps {
  readonly corals: readonly Coral[];
  readonly filteredCorals: readonly Coral[];
  readonly workspace: Workspace;
  readonly setWorkspace: (next: Workspace) => void;
  readonly savedLayouts: readonly SavedLayout[];
  readonly onSaveLayout: (name: string) => void;
  readonly onLoadLayout: (id: string) => void;
  readonly onDeleteLayout: (id: string) => void;
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, n));
}

export function PlacerCanvas({
  corals,
  filteredCorals,
  workspace,
  setWorkspace,
  savedLayouts,
  onSaveLayout,
  onLoadLayout,
  onDeleteLayout,
}: PlacerCanvasProps) {
  const stageRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [fullscreen, setFullscreen] = useState(false);
  const [stageWidth, setStageWidth] = useState(900);
  const [displayMode, setDisplayMode] = useState<DisplayMode>('thumbnail');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Pan / zoom transform.
  const [scale, setScale] = useState(1);
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);

  const panRef = useRef<{ x: number; y: number } | null>(null);
  const dragRef = useRef<{ id: string; moved: boolean } | null>(null);
  const [drag, setDrag] = useState<{ id: string; xPct: number; yPct: number } | null>(null);

  const active = useMemo(
    () => workspace.aquascapes.find((a) => a.id === workspace.activeAquascapeId),
    [workspace.aquascapes, workspace.activeAquascapeId],
  );
  const placements = placementsFor(workspace, workspace.activeAquascapeId);
  const placedCoralIds = new Set(placements.map((p) => p.coralId));
  const selected = placements.find((p) => p.id === selectedId) ?? null;
  const selectedCoral = selected ? findCoral(corals, selected.coralId) : undefined;

  const clampScale = (s: number) => Math.min(8, Math.max(0.4, s));

  useEffect(() => {
    setSelectedId(null);
  }, [workspace.activeAquascapeId]);

  // Non-passive wheel zoom around cursor.
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const cx = e.clientX - rect.left;
      const cy = e.clientY - rect.top;
      const factor = e.deltaY < 0 ? 1.12 : 1 / 1.12;
      setScale((s) => {
        const next = clampScale(s * factor);
        const k = next / s;
        setTx((x) => cx - (cx - x) * k);
        setTy((y) => cy - (cy - y) * k);
        return next;
      });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  // Measure the stage so the aquascape image fills the available width —
  // especially in fullscreen, where there's far more room to see the photo.
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const apply = () => {
      const w = el.clientWidth;
      if (w > 0) setStageWidth(w);
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Re-fit the view to the freshly-sized stage when entering/exiting fullscreen.
  useEffect(() => {
    setScale(1);
    setTx(0);
    setTy(0);
  }, [fullscreen]);

  // Esc exits fullscreen.
  useEffect(() => {
    if (!fullscreen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setFullscreen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [fullscreen]);

  const zoomButton = (factor: number) => {
    const el = stageRef.current;
    const w = el?.clientWidth ?? 600;
    const h = el?.clientHeight ?? 400;
    setScale((s) => {
      const next = clampScale(s * factor);
      const k = next / s;
      setTx((x) => w / 2 - (w / 2 - x) * k);
      setTy((y) => h / 2 - (h / 2 - y) * k);
      return next;
    });
  };
  const resetView = () => {
    setScale(1);
    setTx(0);
    setTy(0);
  };

  // ── Pointer handling (pan background, drag dots) ──────────────────
  const onStagePointerDown = (e: React.PointerEvent) => {
    if (dragRef.current) return;
    panRef.current = { x: e.clientX, y: e.clientY };
    stageRef.current?.setPointerCapture?.(e.pointerId);
  };
  const onStagePointerMove = (e: React.PointerEvent) => {
    if (dragRef.current) {
      const rect = contentRef.current?.getBoundingClientRect();
      if (!rect || rect.width === 0 || rect.height === 0) return;
      const xPct = clamp((e.clientX - rect.left) / rect.width, 0, 1);
      const yPct = clamp((e.clientY - rect.top) / rect.height, 0, 1);
      dragRef.current.moved = true;
      setDrag({ id: dragRef.current.id, xPct, yPct });
      return;
    }
    if (panRef.current) {
      const dx = e.clientX - panRef.current.x;
      const dy = e.clientY - panRef.current.y;
      panRef.current = { x: e.clientX, y: e.clientY };
      setTx((x) => x + dx);
      setTy((y) => y + dy);
    }
  };
  const onStagePointerUp = (e: React.PointerEvent) => {
    if (dragRef.current && drag && dragRef.current.moved) {
      setWorkspace(
        updatePlacement(workspace, workspace.activeAquascapeId, dragRef.current.id, {
          xPct: drag.xPct,
          yPct: drag.yPct,
        }),
      );
    }
    dragRef.current = null;
    setDrag(null);
    panRef.current = null;
    stageRef.current?.releasePointerCapture?.(e.pointerId);
  };

  const onDotPointerDown = (e: React.PointerEvent, p: Placement) => {
    e.stopPropagation();
    setSelectedId(p.id);
    dragRef.current = { id: p.id, moved: false };
    stageRef.current?.setPointerCapture?.(e.pointerId);
  };

  // ── Library → add placement ───────────────────────────────────────
  const addCoral = (coral: Coral) => {
    const placement: Placement = {
      id: genId('pl'),
      coralId: coral.id,
      xPct: 0.5,
      yPct: 0.5,
      sizePct: workspace.defaultDotSize,
      photo: 'colony',
    };
    setWorkspace(addPlacement(workspace, workspace.activeAquascapeId, placement));
    setSelectedId(placement.id);
  };

  // ── Aquascape management ──────────────────────────────────────────
  const onUploadClick = () => fileInputRef.current?.click();
  const onFileChosen = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const src = await fileToDownscaledDataUrl(file);
      const name = file.name.replace(/\.[^.]+$/, '') || 'My aquascape';
      setWorkspace(
        addAquascape(workspace, { id: genId('aq'), name, src }),
      );
    } finally {
      setUploading(false);
    }
  };
  const onRename = () => {
    if (!active) return;
    const next = window.prompt('Rename aquascape', active.name);
    if (next && next.trim() !== '') {
      setWorkspace(renameAquascape(workspace, active.id, next.trim()));
    }
  };
  const onDeleteAquascape = () => {
    if (!active || active.builtIn) return;
    if (window.confirm(`Delete "${active.name}" and its placements?`)) {
      setWorkspace(removeAquascape(workspace, active.id));
    }
  };

  // ── Selected dot controls ─────────────────────────────────────────
  const setSelectedSize = (sizePct: number) => {
    if (!selected) return;
    setWorkspace(
      updatePlacement(workspace, workspace.activeAquascapeId, selected.id, { sizePct }),
    );
  };
  const setSelectedPhoto = (photo: PlacementPhoto) => {
    if (!selected) return;
    setWorkspace(
      updatePlacement(workspace, workspace.activeAquascapeId, selected.id, { photo }),
    );
  };
  const removeSelected = () => {
    if (!selected) return;
    setWorkspace(removePlacement(workspace, workspace.activeAquascapeId, selected.id));
    setSelectedId(null);
  };

  // ── Export PNG ────────────────────────────────────────────────────
  const onExport = async () => {
    if (!active) return;
    setExporting(true);
    try {
      const bg = await loadImageSafe(active.src);
      const w = bg?.naturalWidth || 1600;
      const h = bg?.naturalHeight || 1000;
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      if (bg) ctx.drawImage(bg, 0, 0, w, h);
      else {
        ctx.fillStyle = '#06101f';
        ctx.fillRect(0, 0, w, h);
      }
      for (let i = 0; i < placements.length; i += 1) {
        const p = placements[i];
        if (!p) continue;
        const coral = findCoral(corals, p.coralId);
        const cx = p.xPct * w;
        const cy = p.yPct * h;
        const r = (p.sizePct * w) / 2;
        const ringColor = coral ? COLOR_HEX[coral.colors[0] ?? 'MULTI'] : '#8b5cf6';
        if (displayMode === 'thumbnail' && coral) {
          const photo = await loadImageSafe(placementPhotoUrl(coral, p.photo));
          ctx.save();
          ctx.beginPath();
          ctx.arc(cx, cy, r, 0, Math.PI * 2);
          ctx.closePath();
          ctx.clip();
          if (photo) {
            const side = r * 2;
            ctx.drawImage(photo, cx - r, cy - r, side, side);
          } else {
            ctx.fillStyle = ringColor;
            ctx.fillRect(cx - r, cy - r, r * 2, r * 2);
          }
          ctx.restore();
        } else {
          ctx.beginPath();
          ctx.arc(cx, cy, r, 0, Math.PI * 2);
          ctx.fillStyle = ringColor;
          ctx.fill();
        }
        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, Math.PI * 2);
        ctx.lineWidth = Math.max(2, r * 0.12);
        ctx.strokeStyle = ringColor;
        ctx.stroke();
        ctx.fillStyle = '#ffffff';
        ctx.font = `bold ${Math.max(12, r * 0.7)}px sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(String(i + 1), cx, cy);
      }
      const url = canvas.toDataURL('image/png');
      const a = document.createElement('a');
      a.href = url;
      a.download = `${active.name.replace(/\s+/g, '-').toLowerCase()}-placement.png`;
      a.click();
    } finally {
      setExporting(false);
    }
  };

  const toolbarBtn =
    'rounded-md px-2.5 py-1.5 text-xs text-text-2 hover:bg-surface-glass hover:text-text-1';

  return (
    <div className="flex h-full min-h-0 gap-3">
      {/* Library */}
      <aside className="hidden w-64 flex-shrink-0 flex-col overflow-hidden rounded-xl bg-bg-elevated ring-1 ring-surface-stroke lg:flex">
        <div className="border-b border-surface-stroke px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-text-3">
          Library · click to place
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">
          {filteredCorals.map((coral) => {
            const placed = placedCoralIds.has(coral.id);
            return (
              <button
                key={coral.id}
                type="button"
                onClick={() => addCoral(coral)}
                title={
                  placed
                    ? 'Already placed — click to add another'
                    : 'Click to place'
                }
                className={`flex w-full items-center gap-2 px-2.5 py-1.5 text-left hover:bg-surface-glass ${
                  placed ? 'opacity-45' : ''
                }`}
              >
                <CoralThumb
                  coral={coral}
                  prefer="frag"
                  className="h-9 w-9 flex-shrink-0 rounded-md object-cover"
                />
                <div className="min-w-0 flex-1">
                  <p
                    className={`line-clamp-1 text-xs font-medium ${
                      placed ? 'text-text-3 line-through' : 'text-text-1'
                    }`}
                  >
                    {coral.name}
                  </p>
                  <div className="mt-0.5 flex items-center gap-1.5">
                    <ColorDots colors={coral.colors} />
                  </div>
                </div>
                {placed ? (
                  <span
                    aria-label="placed"
                    className="flex-shrink-0 rounded-full bg-ok/20 px-1.5 py-0.5 text-[9px] font-bold text-ok"
                  >
                    ✓
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      </aside>

      {/* Stage */}
      <div
        className={
          fullscreen
            ? 'fixed inset-0 z-[70] flex flex-col bg-bg-base p-3'
            : 'flex min-w-0 flex-1 flex-col'
        }
      >
        {/* Toolbar */}
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <Select
            value={workspace.activeAquascapeId}
            onChange={(e) => setWorkspace(setActiveAquascape(workspace, e.target.value))}
            className="!w-auto min-w-[10rem]"
          >
            {workspace.aquascapes.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </Select>
          <Button size="sm" onClick={onUploadClick} disabled={uploading}>
            {uploading ? 'Uploading…' : '＋ Upload photo'}
          </Button>
          <button type="button" className={toolbarBtn} onClick={onRename}>
            Rename
          </button>
          {active && !active.builtIn && (
            <button type="button" className={toolbarBtn} onClick={onDeleteAquascape}>
              Delete
            </button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => void onFileChosen(e)}
          />

          <div className="ml-auto flex items-center gap-1 rounded-lg bg-bg-elevated p-1 ring-1 ring-surface-stroke">
            <button
              type="button"
              onClick={() => setDisplayMode('thumbnail')}
              className={`rounded px-2 py-1 text-xs ${displayMode === 'thumbnail' ? 'bg-accent text-white' : 'text-text-2'}`}
            >
              Thumbnail
            </button>
            <button
              type="button"
              onClick={() => setDisplayMode('dot')}
              className={`rounded px-2 py-1 text-xs ${displayMode === 'dot' ? 'bg-accent text-white' : 'text-text-2'}`}
            >
              Dot
            </button>
          </div>

          <div className="flex items-center gap-1 rounded-lg bg-bg-elevated p-1 ring-1 ring-surface-stroke">
            <button type="button" aria-label="Zoom out" className={toolbarBtn} onClick={() => zoomButton(1 / 1.2)}>
              −
            </button>
            <button type="button" className={toolbarBtn} onClick={resetView}>
              {Math.round(scale * 100)}%
            </button>
            <button type="button" aria-label="Zoom in" className={toolbarBtn} onClick={() => zoomButton(1.2)}>
              +
            </button>
            <button
              type="button"
              aria-label={fullscreen ? 'Exit fullscreen' : 'Fullscreen'}
              className={toolbarBtn}
              onClick={() => setFullscreen((f) => !f)}
            >
              {fullscreen ? 'Exit' : '⛶ Full'}
            </button>
          </div>
        </div>

        {/* The image stage */}
        <div
          ref={stageRef}
          className="relative min-h-0 flex-1 touch-none overflow-hidden rounded-xl bg-bg-mid ring-1 ring-surface-stroke"
          style={{ cursor: panRef.current ? 'grabbing' : 'grab' }}
          onPointerDown={onStagePointerDown}
          onPointerMove={onStagePointerMove}
          onPointerUp={onStagePointerUp}
          onPointerLeave={onStagePointerUp}
        >
          {!active ? (
            <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
              <p className="max-w-xs text-sm text-text-2">
                No aquascape photo yet. Upload a photo of your rockwork to start
                placing corals.
              </p>
              <Button size="sm" onClick={onUploadClick} disabled={uploading}>
                {uploading ? 'Uploading…' : '＋ Upload photo'}
              </Button>
            </div>
          ) : (
            <div
              className="absolute left-0 top-0 origin-top-left"
              style={{ transform: `translate(${tx}px, ${ty}px) scale(${scale})` }}
            >
              <div
                ref={contentRef}
                className="relative max-w-none select-none"
                style={{ width: `${stageWidth}px` }}
              >
                <img
                  src={active.src}
                  alt={active.name}
                  className="block w-full"
                  draggable={false}
                />
                {placements.map((p, i) => {
                  const coral = findCoral(corals, p.coralId);
                  const pos = drag && drag.id === p.id ? drag : p;
                  const isSel = p.id === selectedId;
                  const ring = coral ? COLOR_HEX[coral.colors[0] ?? 'MULTI'] : '#8b5cf6';
                  return (
                    <div
                      key={p.id}
                      onPointerDown={(e) => onDotPointerDown(e, p)}
                      className="absolute touch-none"
                      style={{
                        left: `${pos.xPct * 100}%`,
                        top: `${pos.yPct * 100}%`,
                        width: `${p.sizePct * 100}%`,
                        aspectRatio: '1 / 1',
                        transform: 'translate(-50%, -50%)',
                        cursor: 'grab',
                      }}
                    >
                      <div
                        className="relative flex h-full w-full items-center justify-center overflow-hidden rounded-full"
                        style={{
                          boxShadow: isSel
                            ? `0 0 0 3px #ff6a3d, 0 0 0 5px ${ring}`
                            : `0 0 0 3px ${ring}`,
                          backgroundColor: ring,
                        }}
                      >
                        {displayMode === 'thumbnail' && coral ? (
                          <CoralThumb
                            coral={coral}
                            prefer={p.photo}
                            className="h-full w-full object-cover"
                          />
                        ) : null}
                        <span
                          className="absolute bottom-0 right-0 rounded-tl bg-black/60 px-1 text-[10px] font-bold text-white"
                          style={{ fontSize: 'clamp(8px, 1.2vw, 13px)' }}
                        >
                          {i + 1}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Right panel */}
      <aside className="hidden w-72 flex-shrink-0 flex-col overflow-hidden rounded-xl bg-bg-elevated ring-1 ring-surface-stroke xl:flex">
        <div className="border-b border-surface-stroke px-3 py-2">
          <p className="text-sm font-semibold text-text-1">{active?.name ?? '—'}</p>
          <p className="text-xs text-text-3">
            {placements.length} placement{placements.length === 1 ? '' : 's'} · saved to Pi
          </p>
        </div>

        {/* Selected dot controls */}
        {selected && selectedCoral && (
          <div className="space-y-3 border-b border-surface-stroke px-3 py-3">
            <div className="flex items-center gap-2">
              <CoralThumb
                coral={selectedCoral}
                prefer={selected.photo}
                className="h-10 w-10 rounded-md object-cover"
              />
              <div className="min-w-0">
                <p className="line-clamp-1 text-sm font-medium text-text-1">
                  {selectedCoral.name}
                </p>
                <GrowthPill form={selectedCoral.growthForm} />
              </div>
            </div>
            <label className="block">
              <span className="mb-1 flex justify-between text-xs text-text-2">
                <span>Dot size</span>
                <span>{Math.round(selected.sizePct * 100)}%</span>
              </span>
              <input
                type="range"
                min={MIN_DOT_SIZE * 100}
                max={MAX_DOT_SIZE * 100}
                value={selected.sizePct * 100}
                onChange={(e) => setSelectedSize(Number(e.target.value) / 100)}
                className="w-full accent-accent"
              />
            </label>
            <div>
              <span className="mb-1 block text-xs text-text-2">Photo</span>
              <div className="flex gap-1">
                {(['frag', 'colony'] as const).map((which) => (
                  <button
                    key={which}
                    type="button"
                    onClick={() => setSelectedPhoto(which)}
                    className={`flex-1 rounded-md px-2 py-1 text-xs capitalize ring-1 ${
                      selected.photo === which
                        ? 'bg-accent text-white ring-accent'
                        : 'bg-surface-glass text-text-2 ring-surface-stroke'
                    }`}
                  >
                    {which}
                  </button>
                ))}
              </div>
            </div>
            <Button variant="danger" size="sm" className="w-full" onClick={removeSelected}>
              Remove dot
            </Button>
          </div>
        )}

        {/* Placement list */}
        <div className="min-h-0 flex-1 overflow-y-auto">
          {placements.length === 0 ? (
            <p className="px-3 py-6 text-center text-xs text-text-3">
              Click a coral in the library to place it.
            </p>
          ) : (
            placements.map((p, i) => {
              const coral = findCoral(corals, p.coralId);
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setSelectedId(p.id)}
                  className={`flex w-full items-center gap-2 px-3 py-1.5 text-left ${
                    p.id === selectedId ? 'bg-surface-glass' : 'hover:bg-surface-glass'
                  }`}
                >
                  <span className="w-5 text-center text-xs text-text-3">{i + 1}</span>
                  {coral ? (
                    <CoralThumb
                      coral={coral}
                      prefer={p.photo}
                      className="h-7 w-7 rounded object-cover"
                    />
                  ) : null}
                  <span className="line-clamp-1 flex-1 text-xs text-text-2">
                    {coral?.name ?? p.coralId}
                  </span>
                </button>
              );
            })
          )}
        </div>

        {/* Saved layouts */}
        <div className="border-t border-surface-stroke px-3 py-3">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-text-3">
              Saved layouts
            </span>
            <button
              type="button"
              onClick={() => {
                const suggestion = active ? `${active.name} layout` : 'My layout';
                const name = window.prompt('Save current layout as:', suggestion);
                if (name && name.trim() !== '') onSaveLayout(name.trim());
              }}
              className="rounded-md bg-accent px-2 py-1 text-xs font-medium text-white hover:bg-accent-hover"
            >
              ＋ Save current
            </button>
          </div>
          {savedLayouts.length === 0 ? (
            <p className="text-xs text-text-3">
              No saved layouts yet. Save one to reload it later.
            </p>
          ) : (
            <ul className="max-h-40 space-y-1 overflow-y-auto">
              {savedLayouts.map((layout) => (
                <li
                  key={layout.id}
                  className="flex items-center gap-2 rounded-md bg-surface-glass px-2 py-1"
                >
                  <span className="min-w-0 flex-1">
                    <span className="line-clamp-1 text-xs text-text-1">{layout.name}</span>
                    <span className="block text-[10px] text-text-3">
                      {layout.placements.length} coral
                      {layout.placements.length === 1 ? '' : 's'}
                    </span>
                  </span>
                  <button
                    type="button"
                    onClick={() => onLoadLayout(layout.id)}
                    className="rounded px-1.5 py-0.5 text-[11px] font-medium text-accent hover:bg-accent-soft"
                  >
                    Load
                  </button>
                  <button
                    type="button"
                    aria-label={`Delete layout ${layout.name}`}
                    onClick={() => {
                      if (window.confirm(`Delete layout "${layout.name}"?`)) {
                        onDeleteLayout(layout.id);
                      }
                    }}
                    className="rounded px-1 py-0.5 text-[11px] text-bad hover:bg-bad/10"
                  >
                    ✕
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Footer actions */}
        <div className="space-y-2 border-t border-surface-stroke px-3 py-3">
          <label className="block">
            <span className="mb-1 flex justify-between text-xs text-text-2">
              <span>Default dot size</span>
              <span>{Math.round(workspace.defaultDotSize * 100)}%</span>
            </span>
            <input
              type="range"
              min={MIN_DOT_SIZE * 100}
              max={MAX_DOT_SIZE * 100}
              value={workspace.defaultDotSize * 100}
              onChange={(e) =>
                setWorkspace(setDefaultDotSize(workspace, Number(e.target.value) / 100))
              }
              className="w-full accent-accent"
            />
          </label>
          <Button size="sm" className="w-full" onClick={() => void onExport()} disabled={exporting}>
            {exporting ? 'Exporting…' : 'Export as PNG'}
          </Button>
          <button
            type="button"
            onClick={() => {
              if (window.confirm('Clear all placements on this aquascape?')) {
                setWorkspace(clearPlacements(workspace, workspace.activeAquascapeId));
                setSelectedId(null);
              }
            }}
            className="w-full rounded-md py-1.5 text-xs text-bad hover:bg-bad/10"
          >
            Clear all
          </button>
        </div>
      </aside>
    </div>
  );
}

function loadImageSafe(src: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}
